import React, { useCallback } from 'react';
import ProductCard from '../card/ProductCard';
import { Box, IconButton, useTheme } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

function Index(props) {
    const { giftCardData, fixSlideView } = props;
    const theme = useTheme();
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        containScroll: 'trimSnaps',
        slidesToScroll: 1, // Scroll one column at a time
        dragFree: true
    });

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    // Chunk data into pairs for 2-row layout
    const chunkedData = React.useMemo(() => {
        if (!giftCardData) return [];
        const chunks = [];
        for (let i = 0; i < giftCardData.length; i += 2) {
            chunks.push(giftCardData.slice(i, i + 2));
        }
        return chunks;
    }, [giftCardData]);

    // Define responsive bases based on fixSlideView logic
    const getFlexBasis = () => {
        if (fixSlideView) {
            return {
                xs: '33.33%', // smDevice (max 900) -> 3
                md: '25%',    // mdDevice (max 1200) -> 4
                lg: '20%',    // lgDevice (max 1535) -> 5
                xl: '20%'     // else -> 5
            };
        } else {
            return {
                xs: '50%',      // xsDevice (max 600) -> 2
                sm: '33.333%',  // smDevice (max 900) -> 3
                md: '20%',      // mdDevice (max 1200) -> 5
                lg: '33.333%',  // lgDevice (max 1535) -> 3
                xl: '25%'       // else -> 4
            };
        }
    };

    const flexBasis = getFlexBasis();

    return (
        <Box sx={{
            height: 'auto',
            position: 'relative',
            px: { xs: 0, sm: 1 }
        }}>
            <Box sx={{ overflow: 'hidden' }} ref={emblaRef}>
                <Box sx={{
                    display: 'flex',
                    // marginLeft: '-20px' // Compensate for padding
                }}>
                    {chunkedData.map((pair, index) => (
                        <Box
                            key={`slide-${index}`}
                            sx={{
                                flex: {
                                    xs: `0 0 ${flexBasis.xs}`,
                                    sm: `0 0 ${flexBasis.sm}`,
                                    md: `0 0 ${flexBasis.md}`,
                                    lg: `0 0 ${flexBasis.lg}`,
                                    xl: `0 0 ${flexBasis.xl}`,
                                },
                                minWidth: 0,
                                pl: '20px', // Gap between columns
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px' // Gap between rows
                            }}
                        >
                            {pair.map((item) => (
                                <Box key={`gift-card-${item?.id}`} sx={{ flex: 1 }}>
                                    <ProductCard
                                        customStyles={customStyles}
                                        data={item}
                                    />
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Navigation Buttons */}
            <IconButton
                onClick={scrollPrev}
                sx={{
                    position: 'absolute',
                    left: { xs: -10, md: -5 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'primary.main',
                    color: 'white.main',
                    width: 32,
                    height: 32,
                    boxShadow: 2,
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                    },
                    display: { xs: 'none', md: 'flex' }
                }}
            >
                <ArrowBackIosNewIcon fontSize="small" sx={{ fontSize: '1rem' }} />
            </IconButton>

            <IconButton
                onClick={scrollNext}
                sx={{
                    position: 'absolute',
                    right: { xs: -10, md: -5 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'primary.main',
                    color: 'white.main',
                    width: 32,
                    height: 32,
                    boxShadow: 2,
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                    },
                    display: { xs: 'none', md: 'flex' }
                }}
            >
                <ArrowForwardIosIcon fontSize="small" sx={{ fontSize: '1rem' }} />
            </IconButton>
        </Box>
    );
}

const customStyles = {
    minWidth: {
        xs: "170px",
        sm: "130px",
        md: "130px",
        lg: "110px",
    },
    maxWidth: {
        xs: "170px",
        sm: "195px",
        md: "180px",
        lg: "180px",
    },
    height: {
        xs: "190px",
        sm: "210px",
        md: "210px",
        lg: "210px",
        xl: "213px",
    },
    maxHeight: {
        xs: "190px",
        sm: "266px",
        md: "247px",
        lg: "260px",
        xl: "255px",
    },
    borderRadius: {
        xs: 6,
        sm: 4,
        lg: 5
    },
    boxShadow: {
        xs: 2,
        sm: "inset 0 0 10px #dee5eb",
        lg: 2
    },
    border: {
        xs: 0,
        sm: "3px solid #deebff"
    },
    p: { xl: "0 0 5px 0" },
    mx: { xs: "3px", sm: "3px" },
    my: {
        xs: "5px",
        sm: "3px",
        lg: "5px",
    },
    mb: {
        xs: "10px",
        sm: "3px",
        lg: "5px",
    },
    cardMedia: {
        p: 0,
        width: "100%",
        minHeight: { xs: 113, sm: 120, md: 120, lg: 115, xl: 115 },
        maxHeight: { xs: 151, sm: 122, md: 130, lg: 116, xl: 116 },
        borderRadius: "0 0 10px 10px",
        objectFit: {
            xs: "fill",
            sm: "cover",
            md: "contain",
            lg: "cover"
        },
        backgroundSize: {
            xs: "fill",
            sm: "cover",
            md: "contain",
            lg: "cover"
        },
        objectPosition: "50% 50%",
        mixBlendMode: "multiply"
    },
    ".product__discount": {
        width: "100%",
        p: "3px 0"
    },
    boughtUser: {
        fontSize: {
            xs: "10px",
            lg: "10px"
        },
        color: {
            xs: "textPrimary.main",
            lg: "textPrimary.main"
        },
        fontWeight: {
            xs: 400,
            lg: 400
        },
        display: {
            xs: "none",
            sm: "none"
        }
    },
    cardContent: {
        mt: "16px",
        py: { xs: 0 },
        px: {
            xs: "10px",
            sm: "15px"
        },
        height: {
            xs: "max-content",
            sm: "80px",
            md: "80px",
            lg: "65px",
        },
        overFlow: "hidden",
        webkitBoxOrient: "horizontal",
        webkitLineClamp: 2,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        textAlign: {
            xs: "center",
            sm: "left",
            lg: "center"
        },
        mb: "5px",
    },
    title: {
        color: {
            xs: "primary.main",
            sm: "primary.main",
            lg: "primary.main"
        },
        fontSize: {
            xs: "14px",
            sm: "1.2rem",
            md: "1.2rem",
            lg: "1.2rem",
            xl: "14px",
        },
        fontWeight: { xs: 500, sm: 500, lg: 500, xl: 500 },
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        webkitLineClamp: 2,
        maxHeight: "3.6rem",
        lineHeight: { xs: "24px", md: "22px" },
        overflowWrap: "break-word",
        wordBreak: "normal",
        textAlign: "center"
    },
    button: {
        width: "100%",
        fontSize: {
            xs: "9px",
            sm: "10px",
            lg: "12px"
        },
        color: {
            xs: "white.main",
            sm: "white.main",
        },
        backgroundColor: {
            xs: "primary.main",
            sm: "primary.main",
        },
        textTransform: {
            xs: "capitalize"
        },
        borderRadius: {
            xs: 5,
            sm: 5,
            lg: 1
        }
    },
    cardActions: {
        px: 2,
        display: {
            xs: "none",
            sm: "none"
        },
        flexDirection: "column",
        alignItems: "flex-start",
        py: 0,
    },
}

export default Index
